/**
 * TipTap Rich Text Editor Component
 *
 * Purpose: Modern WYSIWYG rich text editor using raw @tiptap/react
 * Updated: January 7, 2026
 *
 * Features:
 * - Rich text formatting (bold, italic, underline, etc.)
 * - Headers, lists, links
 * - Built on @tiptap/react with shadcn UI components
 * - Theme-aware and accessible
 * - DOMPurify integration for XSS protection
 */

import * as React from 'react';
import { AnyExtension, JSONContent } from '@tiptap/core';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import DOMPurify from 'dompurify';
import { cn } from '../lib/utils';
import { Label } from './label';
import { Button } from './button';
import { Toggle } from './toggle';
import { Separator } from './separator';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Input } from './input';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  Unlink,
  Image as ImageIcon,
  Undo,
  Redo,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Highlighter,
  Table as TableIcon,
  Minus,
  Type,
  Palette,
} from 'lucide-react';

export interface TiptapEditorProps {
  value?: string;
  onChange?: (_value: string) => void;
  /** When outputFormat is 'json', this callback receives the Tiptap JSON document on every change. */
  onJsonChange?: (_json: JSONContent) => void;
  /** Initialize editor from Tiptap JSON content. Takes precedence over value when provided. */
  jsonValue?: JSONContent | null;
  /**
   * Output format for editor updates.
   * - 'html' (default): emits sanitized HTML via onChange
   * - 'json': emits Tiptap JSON via onJsonChange and can also emit sanitized HTML via onChange for dual-write migration support
   */
  outputFormat?: 'html' | 'json';
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  label?: string;
  id?: string;
  error?: string;
  required?: boolean;
  minHeight?: string | number;
  maxHeight?: string | number;
  disabled?: boolean;
  darkTheme?: boolean;
  charCount?: number;
}

interface ToolbarProps {
  editor: Editor | null;
  disabled?: boolean;
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, disabled }) => {
  const [linkUrl, setLinkUrl] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');

  if (!editor) return null;

  const addLink = () => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
      setLinkUrl('');
    }
  };

  const addImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
    }
  };

  const insertTable = () => {
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  return (
    <div className='flex flex-wrap items-center gap-1 p-2 border-b border-input bg-muted/30'>
      {/* History */}
      <Toggle
        size='sm'
        pressed={false}
        onPressedChange={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
        aria-label='Undo'
      >
        <Undo className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={false}
        onPressedChange={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
        aria-label='Redo'
      >
        <Redo className='h-4 w-4' />
      </Toggle>

      <Separator orientation='vertical' className='h-6 mx-1' />

      {/* Headings */}
      <Toggle
        size='sm'
        pressed={editor.isActive('heading', { level: 1 })}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 1 }).run()
        }
        disabled={disabled}
        aria-label='Heading 1'
      >
        <Heading1 className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('heading', { level: 2 })}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
        disabled={disabled}
        aria-label='Heading 2'
      >
        <Heading2 className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('heading', { level: 3 })}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 3 }).run()
        }
        disabled={disabled}
        aria-label='Heading 3'
      >
        <Heading3 className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('heading', { level: 4 })}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 4 }).run()
        }
        disabled={disabled}
        aria-label='Heading 4'
      >
        <Heading4 className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('paragraph')}
        onPressedChange={() => editor.chain().focus().setParagraph().run()}
        disabled={disabled}
        aria-label='Paragraph'
      >
        <Type className='h-4 w-4' />
      </Toggle>

      <Separator orientation='vertical' className='h-6 mx-1' />

      {/* Text formatting */}
      <Toggle
        size='sm'
        pressed={editor.isActive('bold')}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
        aria-label='Bold'
      >
        <Bold className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('italic')}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
        aria-label='Italic'
      >
        <Italic className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('underline')}
        onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
        disabled={disabled}
        aria-label='Underline'
      >
        <UnderlineIcon className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('strike')}
        onPressedChange={() => editor.chain().focus().toggleStrike().run()}
        disabled={disabled}
        aria-label='Strikethrough'
      >
        <Strikethrough className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('highlight')}
        onPressedChange={() => editor.chain().focus().toggleHighlight().run()}
        disabled={disabled}
        aria-label='Highlight'
      >
        <Highlighter className='h-4 w-4' />
      </Toggle>

      <Separator orientation='vertical' className='h-6 mx-1' />

      {/* Text Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant='ghost' size='sm' disabled={disabled}>
            <Palette className='h-4 w-4' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-2' align='start'>
          <div className='grid grid-cols-8 gap-1'>
            {[
              '#000000',
              '#434343',
              '#666666',
              '#999999',
              '#b7b7b7',
              '#cccccc',
              '#d9d9d9',
              '#ffffff',
              '#980000',
              '#ff0000',
              '#ff9900',
              '#ffff00',
              '#00ff00',
              '#00ffff',
              '#4a86e8',
              '#0000ff',
              '#9900ff',
              '#ff00ff',
              '#e6b8af',
              '#f4cccc',
              '#fce5cd',
              '#fff2cc',
              '#d9ead3',
              '#d0e0e3',
            ].map((color) => (
              <button
                key={color}
                className='w-6 h-6 rounded border border-input hover:scale-110 transition-transform'
                style={{ backgroundColor: color }}
                onClick={() => editor.chain().focus().setColor(color).run()}
              />
            ))}
          </div>
          <Button
            variant='ghost'
            size='sm'
            className='w-full mt-2'
            onClick={() => editor.chain().focus().unsetColor().run()}
          >
            Reset
          </Button>
        </PopoverContent>
      </Popover>

      <Separator orientation='vertical' className='h-6 mx-1' />

      {/* Lists */}
      <Toggle
        size='sm'
        pressed={editor.isActive('bulletList')}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
        aria-label='Bullet List'
      >
        <List className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive('orderedList')}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
        aria-label='Ordered List'
      >
        <ListOrdered className='h-4 w-4' />
      </Toggle>

      <Separator orientation='vertical' className='h-6 mx-1' />

      {/* Text Alignment */}
      <Toggle
        size='sm'
        pressed={editor.isActive({ textAlign: 'left' })}
        onPressedChange={() =>
          editor.chain().focus().setTextAlign('left').run()
        }
        disabled={disabled}
        aria-label='Align Left'
      >
        <AlignLeft className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive({ textAlign: 'center' })}
        onPressedChange={() =>
          editor.chain().focus().setTextAlign('center').run()
        }
        disabled={disabled}
        aria-label='Align Center'
      >
        <AlignCenter className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive({ textAlign: 'right' })}
        onPressedChange={() =>
          editor.chain().focus().setTextAlign('right').run()
        }
        disabled={disabled}
        aria-label='Align Right'
      >
        <AlignRight className='h-4 w-4' />
      </Toggle>
      <Toggle
        size='sm'
        pressed={editor.isActive({ textAlign: 'justify' })}
        onPressedChange={() =>
          editor.chain().focus().setTextAlign('justify').run()
        }
        disabled={disabled}
        aria-label='Justify'
      >
        <AlignJustify className='h-4 w-4' />
      </Toggle>

      <Separator orientation='vertical' className='h-6 mx-1' />

      {/* Link */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className={cn(editor.isActive('link') && 'bg-accent')}
            disabled={disabled}
          >
            <LinkIcon className='h-4 w-4' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-80' align='start'>
          <div className='flex flex-col gap-2'>
            <Label>URL</Label>
            <Input
              placeholder='https://example.com'
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
            <Button size='sm' onClick={addLink}>
              Add Link
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {editor.isActive('link') && (
        <Toggle
          size='sm'
          pressed={false}
          onPressedChange={() => editor.chain().focus().unsetLink().run()}
          disabled={disabled}
          aria-label='Remove Link'
        >
          <Unlink className='h-4 w-4' />
        </Toggle>
      )}

      {/* Image */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant='ghost' size='sm' disabled={disabled}>
            <ImageIcon className='h-4 w-4' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-80' align='start'>
          <div className='flex flex-col gap-2'>
            <Label>Image URL</Label>
            <Input
              placeholder='https://example.com/image.jpg'
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
            <Button size='sm' onClick={addImage}>
              Add Image
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Table */}
      <Toggle
        size='sm'
        pressed={false}
        onPressedChange={insertTable}
        disabled={disabled}
        aria-label='Insert Table'
      >
        <TableIcon className='h-4 w-4' />
      </Toggle>

      {/* Horizontal Rule */}
      <Toggle
        size='sm'
        pressed={false}
        onPressedChange={() => editor.chain().focus().setHorizontalRule().run()}
        disabled={disabled}
        aria-label='Horizontal Rule'
      >
        <Minus className='h-4 w-4' />
      </Toggle>
    </div>
  );
};

const TiptapEditor = React.forwardRef<HTMLDivElement, TiptapEditorProps>(
  (
    {
      value = '',
      onChange,
      onJsonChange,
      jsonValue,
      outputFormat = 'html',
      placeholder = 'Type something...',
      className,
      readOnly = false,
      label,
      id,
      error,
      required = false,
      minHeight = 100,
      maxHeight,
      disabled = false,
      charCount,
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const editorId = id || generatedId;

    const extensions: AnyExtension[] = [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder,
      }),
      CharacterCount.configure(charCount ? { limit: charCount } : {}),
    ];

    const editor = useEditor({
      extensions,
      content: jsonValue ?? value,
      autofocus: true,

      editable: !readOnly && !disabled,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const sanitizedContent = displayTiptapContent(html).__html;

        if (outputFormat === 'json') {
          onJsonChange?.(editor.getJSON());
        }

        onChange?.(sanitizedContent);
      },
      editorProps: {
        attributes: {
          class: cn(
            'prose prose-sm dark:prose-invert max-w-none focus:outline-none p-4',
            'min-h-[var(--editor-min-height)]',
            maxHeight && 'max-h-[var(--editor-max-height)] overflow-y-auto',
          ),
          style: `--editor-min-height: ${
            typeof minHeight === 'number' ? `${minHeight}px` : minHeight
          }; --editor-max-height: ${
            typeof maxHeight === 'number'
              ? `${maxHeight}px`
              : maxHeight || 'none'
          }`,
        },
      },
    });

    // Update editor content when value prop changes externally (HTML mode)
    React.useEffect(() => {
      if (editor && outputFormat !== 'json' && value !== editor.getHTML()) {
        editor.commands.setContent(value);
      }
    }, [value, editor, outputFormat]);

    // Update editor content when jsonValue prop changes externally (JSON mode)
    React.useEffect(() => {
      if (
        editor &&
        outputFormat === 'json' &&
        jsonValue != null &&
        JSON.stringify(jsonValue) !== JSON.stringify(editor.getJSON())
      ) {
        editor.commands.setContent(jsonValue);
      }
    }, [jsonValue, editor, outputFormat]);

    // Update editable state when readOnly or disabled changes
    React.useEffect(() => {
      if (editor) {
        editor.setEditable(!readOnly && !disabled);
      }
    }, [editor, readOnly, disabled]);

    return (
      <div className='space-y-2' ref={ref}>
        {label && (
          <Label
            htmlFor={editorId}
            className={cn(
              required &&
                "after:content-['*'] after:text-destructive after:ml-1",
            )}
          >
            {label}
          </Label>
        )}
        <div
          className={cn(
            'tiptap-editor-wrapper border border-input rounded-md bg-background overflow-hidden',
            'transition-all duration-300',
            'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
            error && 'border-destructive',
            (readOnly || disabled) && 'opacity-60 cursor-not-allowed',
            className,
          )}
        >
          {!readOnly && <Toolbar editor={editor} disabled={disabled} />}
          <EditorContent editor={editor} id={editorId} />
          {charCount && editor && getCharacterCount(editor) !== null && (
            <div className='flex justify-end px-4 py-2 border-t border-input text-xs text-muted-foreground'>
              {getCharacterCount(editor)} / {charCount} characters
            </div>
          )}
        </div>
        {error && <p className='text-sm text-destructive'>{error}</p>}
      </div>
    );
  },
);

TiptapEditor.displayName = 'TiptapEditor';

function getCharacterCount(editor: Editor): number | null {
  const storage: unknown = editor.storage;

  if (typeof storage !== 'object' || storage === null) {
    return null;
  }

  const characterCount = (storage as Record<string, unknown>).characterCount;

  if (typeof characterCount !== 'object' || characterCount === null) {
    return null;
  }

  const characters = (characterCount as { characters?: unknown }).characters;

  if (typeof characters !== 'function') {
    return null;
  }

  const value = (characters as () => number)();
  return typeof value === 'number' ? value : null;
}

// Utility function to display sanitized HTML content
export const displayTiptapContent = (html: string) => {
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'b',
      'p',
      'br',
      'strong',
      'em',
      'u',
      'ol',
      'ul',
      'li',
      'a',
      'blockquote',
      'code',
      'pre',
      'hr',
      'i',
      'div',
      'span',
      's',
      'strike',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'img',
      'colgroup',
      'col',
      'mark',
    ],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'class',
      'style',
      'data-*',
      'colspan',
      'rowspan',
      'src',
      'alt',
      'title',
      'width',
      'height',
    ],
    ALLOW_DATA_ATTR: true,
  });

  return { __html: sanitized };
};

export { TiptapEditor };
